import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import { FC, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import strings from '../../../common/Translation/Translate';
import axiosInstance from '../../../utils/axios';
import { privileges } from '../../../utils/constants/privileges';
import { routes } from '../../../utils/constants/routes';
import { URLS } from '../../../utils/constants/urls';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import ListPage from '../CommonPage';
import { lastUpdatedTableColumn } from '../CommonPage/lastUpdatedColumn';

const allocationStatusLabel = (status?: string | null) => {
  switch (status) {
    case 'FULLY_ALLOCATED':
      return 'Fully Allocated';
    case 'PARTIALLY_ALLOCATED':
      return 'Partially Allocated';
    case 'NOT_ALLOCATED':
    default:
      return 'Not Allocated';
  }
};

const BudgetList: FC = () => {
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const pageConfig = {
    title: 'Budgets',
    entityLabel: 'Budgets',
    subtitle: 'Set monthly targets and track allocation across dimensions.',
    searchField: 'name',
    searchPlaceholder: 'Search budgets by name',
    AddButtonText: strings.budget_button_add,
    AddButtonUrl: routes.addBudget,
    editButtonUrl: routes.editBudget,
    listApi: URLS.budgets,
    deleteApi: URLS.deleteBudget,
    addPrivilege: privileges.add_budget,
    editPrivilege: privileges.edit_budget,
    deletePrivilege: privileges.delete_budget,
    tableColumn: [
      {
        field: 'name',
        headerName: strings.budget_text_name,
        flex: 1.4,
        sortable: true,
        renderCell: (value: string) => (
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{value || '—'}</Typography>
        ),
      },
      {
        field: 'month',
        localField: 'startDate',
        headerName: strings.budget_text_month,
        flex: 1.1,
        sortable: true,
        renderCell: (value: string) => (
          <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.35 }}>{value || '—'}</Typography>
        ),
      },
      {
        field: 'formattedTarget',
        localField: 'target',
        headerName: strings.budget_text_target,
        flex: 1.2,
        sortable: true,
        textAlign: 'right',
        renderCell: (_value: any, row: any) => (
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.35, textAlign: 'right' }}>
            {row.formattedTarget || '—'}
          </Typography>
        ),
      },
      {
        field: 'allocationStatus',
        headerName: 'Allocation Status',
        flex: 1.6,
        sortable: false,
        renderCell: (_value: any, row: any) => {
          const configured = Number(row.configuredDimensionCount ?? 0);
          const total = Number(row.totalDimensionCount ?? 4);
          return (
            <>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1.35 }}>
                {allocationStatusLabel(row.allocationStatus)}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.35 }}>
                {configured}/{total} dimensions configured
              </Typography>
            </>
          );
        },
      },
      lastUpdatedTableColumn(),
    ],
  };

  const getList = useCallback(
    async (filterData: any) => {
      try {
        setLoading(true);
        const body = filterData ?? { page: index + 1, size: pageSize, filter: filter, sort: ['startDate:DESC'] };
        const { data } = await axiosInstance.post(pageConfig.listApi, body);
        const format = data.content?.map((item: any) => {
          return {
            ...item,
            month: item.startDate ? dayjs(item.startDate).format('MMM YYYY') : '—',
          };
        });
        setPageResponse({ ...data, content: format });
      } catch (error: any) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [pageConfig.listApi, index, pageSize, filter],
  );

  useEffect(() => {
    (async () => {
      await getList(null);
    })();
  }, [getList]);

  return <ListPage isLoading={loading} pageConfig={pageConfig} pageResponse={pageResponse} getList={getList} />;
};
export default BudgetList;
